import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  HiArchive,
  HiOutlineUserGroup,
  HiPlus,
  HiSearch,
  HiTrash,
  HiUser,
  HiX,
} from 'react-icons/hi';
import apiService from '../services/api';
import ConfirmDialog from './ConfirmDialog';
import Notification from './Notification';
import { useNotification } from '../hooks/useNotification';
import { useRealtimeRefresh } from '../hooks/useRealtimeRefresh';
import { normalizeApiData } from '../utils/apiHelpers';
import './OrganizarCaixa.css';

const debounce = (fn, ms) => {
  let t;
  return (...args) => {
    window.clearTimeout(t);
    t = window.setTimeout(() => fn(...args), ms);
  };
};

const pacoteCode = (pkg) =>
  pkg?.codigo_pacote || pkg?.codigo || (pkg?.id != null ? `#${pkg.id}` : 'CX');

const pacoteCliente = (pkg) =>
  pkg?.cliente?.nome || pkg?.nome_destinatario || 'Cliente';

const newestFirst = (a, b, dateKey = 'created_at') => {
  const tb = Date.parse(b?.[dateKey] || b?.created_at || '') || 0;
  const ta = Date.parse(a?.[dateKey] || a?.created_at || '') || 0;
  if (tb !== ta) return tb - ta;
  return (Number(b?.id) || 0) - (Number(a?.id) || 0);
};

const byNewest = (list, dateKey = 'created_at') =>
  [...(list || [])].sort((a, b) => newestFirst(a, b, dateKey));

const OrganizarCaixa = () => {
  const {
    notifications,
    removeNotification,
    showError,
    showSuccess,
    showWarning,
  } = useNotification();

  const [invoiceQuery, setInvoiceQuery] = useState('');
  const [invoiceResults, setInvoiceResults] = useState([]);
  const [searchingInvoices, setSearchingInvoices] = useState(false);
  const [showInvoiceMenu, setShowInvoiceMenu] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const [board, setBoard] = useState(null);
  const [loadingBoard, setLoadingBoard] = useState(false);
  const [selectedPacoteId, setSelectedPacoteId] = useState(null);
  const [pacoteFilter, setPacoteFilter] = useState('');
  const [creatingBox, setCreatingBox] = useState(false);
  const [savingBoxId, setSavingBoxId] = useState(null);
  const [obsDrafts, setObsDrafts] = useState({});
  const [pkgObsDrafts, setPkgObsDrafts] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [draggingPacoteId, setDraggingPacoteId] = useState(null);
  const [dropBoxId, setDropBoxId] = useState(null);
  const [addMenuBoxId, setAddMenuBoxId] = useState(null);
  const [addMenuQuery, setAddMenuQuery] = useState('');

  const searchWrapRef = useRef(null);
  const addMenuRef = useRef(null);
  const invoiceId = selectedInvoice?.id;

  const loadBoard = useCallback(
    async (id = invoiceId, { silent } = {}) => {
      if (!id) {
        setBoard(null);
        return;
      }
      if (!silent) setLoadingBoard(true);
      try {
        const json = await apiService.getCaixaFisicaBoard(id);
        const data = json?.data || json;
        setBoard(data);
        const drafts = {};
        (data?.caixas || []).forEach((caixa) => {
          drafts[caixa.id] = caixa.observacao || '';
        });
        setObsDrafts(drafts);
        const pkgDrafts = {};
        (data?.caixas || []).forEach((caixa) => {
          (caixa.pacotes || []).forEach((pkg) => {
            pkgDrafts[`${caixa.id}:${pkg.id}`] = pkg.observacao || '';
          });
        });
        setPkgObsDrafts(pkgDrafts);
      } catch (error) {
        showError(error.message || 'Erro ao carregar o quadro');
        setBoard(null);
      } finally {
        if (!silent) setLoadingBoard(false);
      }
    },
    [invoiceId, showError]
  );

  useRealtimeRefresh(
    ['caixa-fisicas', 'packages', 'invoices'],
    () => loadBoard(invoiceId, { silent: true }),
    { enabled: Boolean(invoiceId), paused: Boolean(deleteTarget) }
  );

  const searchInvoices = useMemo(
    () =>
      debounce(async (term) => {
        const q = String(term || '').trim();
        setSearchingInvoices(true);
        try {
          const data = await apiService.getInvoicesList({
            search: q || undefined,
            per_page: 20,
          });
          const list = normalizeApiData(data);
          setInvoiceResults(Array.isArray(list) ? list : []);
        } catch (error) {
          setInvoiceResults([]);
        } finally {
          setSearchingInvoices(false);
        }
      }, 280),
    []
  );

  useEffect(() => {
    searchInvoices(invoiceQuery);
  }, [invoiceQuery, searchInvoices]);

  useEffect(() => {
    if (invoiceId) loadBoard(invoiceId);
  }, [invoiceId, loadBoard]);

  useEffect(() => {
    const onDocClick = (event) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(event.target)) {
        setShowInvoiceMenu(false);
      }
      if (addMenuRef.current && !addMenuRef.current.contains(event.target)) {
        setAddMenuBoxId(null);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const caixas = useMemo(
    () => byNewest(board?.caixas || []),
    [board]
  );
  const pacotes = useMemo(
    () => byNewest(board?.pacotes || []),
    [board]
  );
  const resumo = board?.resumo || {};
  const invoice = board?.invoice || selectedInvoice;

  const filteredPacotes = useMemo(() => {
    const q = pacoteFilter.trim().toLowerCase();
    if (!q) return pacotes;
    return pacotes.filter((pkg) => {
      const hay = [
        pacoteCode(pkg),
        pacoteCliente(pkg),
        pkg.cliente?.codigo,
        pkg.destino,
        ...(pkg.numeros_caixa || []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [pacotes, pacoteFilter]);

  const pendingCount = resumo.pacotes_pendentes ?? 0;

  const pickInvoice = (inv) => {
    setSelectedInvoice(inv);
    setInvoiceQuery(inv.numero_documento || '');
    setShowInvoiceMenu(false);
    setSelectedPacoteId(null);
    setAddMenuBoxId(null);
    setAddMenuQuery('');
  };

  const handleCreateBox = async () => {
    if (!invoiceId) return;
    setCreatingBox(true);
    try {
      const ids = selectedPacoteId ? [selectedPacoteId] : [];
      await apiService.createCaixaFisica({
        invoice_id: invoiceId,
        ocupacao: 'exclusivo',
        pacote_ids: ids,
      });
      showSuccess(ids.length ? 'Caixa física criada com o CX selecionado' : 'Caixa física criada');
      setSelectedPacoteId(null);
      await loadBoard(invoiceId, { silent: true });
    } catch (error) {
      showError(error.message || 'Erro ao criar caixa física');
    } finally {
      setCreatingBox(false);
    }
  };

  const handleOcupacao = (caixa, ocupacao) => {
    const count = (caixa.pacotes || []).length;
    const required = count > 1 ? 'misturado' : 'exclusivo';
    if (ocupacao !== required) {
      showWarning(
        count > 1
          ? 'Com mais de um CX a caixa fica Misturado automaticamente'
          : 'Com um CX a caixa fica Só este cliente automaticamente'
      );
    }
  };

  const handleSaveObs = async (caixa) => {
    const next = (obsDrafts[caixa.id] || '').trim();
    if ((caixa.observacao || '') === next) return;
    setSavingBoxId(caixa.id);
    try {
      await apiService.updateCaixaFisica(caixa.id, { observacao: next });
      await loadBoard(invoiceId, { silent: true });
    } catch (error) {
      showError(error.message || 'Erro ao salvar observação');
    } finally {
      setSavingBoxId(null);
    }
  };

  const handleAssign = async (caixaId, pacoteId) => {
    if (!caixaId || !pacoteId) return;
    const caixa = caixas.find((c) => c.id === caixaId);
    if (caixa?.pacotes?.some((p) => p.id === pacoteId)) {
      showWarning('Este CX já está nesta caixa física');
      return;
    }
    setSavingBoxId(caixaId);
    try {
      await apiService.attachCaixaFisicaPacotes(caixaId, [pacoteId]);
      setSelectedPacoteId(null);
      setAddMenuBoxId(null);
      setAddMenuQuery('');
      await loadBoard(invoiceId, { silent: true });
    } catch (error) {
      showError(error.message || 'Erro ao atribuir CX');
    } finally {
      setSavingBoxId(null);
    }
  };

  const toggleAddMenu = (caixaId, event) => {
    event.stopPropagation();
    setAddMenuBoxId((cur) => (cur === caixaId ? null : caixaId));
    setAddMenuQuery('');
  };

  const pacotesDisponiveis = (caixa) => {
    const inBox = new Set((caixa.pacotes || []).map((p) => p.id));
    const q = addMenuQuery.trim().toLowerCase();
    return pacotes
      .filter((pkg) => !inBox.has(pkg.id))
      .filter((pkg) => {
        if (!q) return true;
        const hay = [pacoteCode(pkg), pacoteCliente(pkg), pkg.cliente?.codigo]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      })
      .sort((a, b) => {
        const ua = a.tem_caixa_fisica ? 1 : 0;
        const ub = b.tem_caixa_fisica ? 1 : 0;
        if (ua !== ub) return ua - ub;
        return newestFirst(a, b);
      });
  };

  const handleDetach = async (caixaId, pacoteId) => {
    setSavingBoxId(caixaId);
    try {
      await apiService.detachCaixaFisicaPacote(caixaId, pacoteId);
      await loadBoard(invoiceId, { silent: true });
    } catch (error) {
      showError(error.message || 'Erro ao remover CX');
    } finally {
      setSavingBoxId(null);
    }
  };

  const handleSavePkgObs = async (caixaId, pacoteId) => {
    const key = `${caixaId}:${pacoteId}`;
    const caixa = caixas.find((c) => c.id === caixaId);
    const pkg = caixa?.pacotes?.find((p) => p.id === pacoteId);
    const next = (pkgObsDrafts[key] || '').trim();
    if ((pkg?.observacao || '') === next) return;
    setSavingBoxId(caixaId);
    try {
      await apiService.updateCaixaFisicaPacote(caixaId, pacoteId, next);
      await loadBoard(invoiceId, { silent: true });
    } catch (error) {
      showError(error.message || 'Erro ao salvar observação do CX');
    } finally {
      setSavingBoxId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await apiService.deleteCaixaFisica(deleteTarget.id);
      showSuccess('Caixa física excluída');
      setDeleteTarget(null);
      await loadBoard(invoiceId, { silent: true });
    } catch (error) {
      showError(error.message || 'Erro ao excluir caixa física');
    }
  };

  const onDropPacote = (caixaId) => {
    if (draggingPacoteId) handleAssign(caixaId, draggingPacoteId);
    setDraggingPacoteId(null);
    setDropBoxId(null);
  };

  return (
    <div className="orgcx-page">
      {notifications.map((n) => (
        <Notification
          key={n.id}
          message={n.message}
          type={n.type}
          onClose={() => removeNotification(n.id)}
        />
      ))}

      <header className="orgcx-hero">
        <div className="orgcx-hero__copy">
          <span className="orgcx-hero__icon" aria-hidden>
            <HiArchive size={28} />
          </span>
          <div>
            <p className="orgcx-kicker">Operação de viagem</p>
            <h1>Organizar Caixa</h1>
            <p>
              Atribua cada CX à caixa física em que viajou. O entregador vê
              o número, se está inteiro ou misturado, e qualquer observação
              ao escanear o cliente.
            </p>
          </div>
        </div>
        {invoiceId && (
          <div className="orgcx-stats" aria-label="Resumo da viagem">
            <div className="orgcx-stat">
              <strong>{resumo.total_caixas || 0}</strong>
              <span>Caixas físicas</span>
            </div>
            <div className="orgcx-stat">
              <strong>{resumo.pacotes_organizados || 0}</strong>
              <span>CX organizados</span>
            </div>
            <div className={`orgcx-stat${pendingCount ? ' orgcx-stat--warn' : ''}`}>
              <strong>{pendingCount}</strong>
              <span>Pendentes</span>
            </div>
            <div className="orgcx-stat orgcx-stat--mix">
              <strong>{resumo.caixas_misturadas || 0}</strong>
              <span>Misturadas</span>
            </div>
          </div>
        )}
      </header>

      <section className="orgcx-voyage" ref={searchWrapRef}>
        <div className="orgcx-voyage__label">
          <HiSearch size={18} />
          <span>Viagem / Invoice</span>
        </div>
        <div className="orgcx-voyage__search">
          <input
            type="search"
            value={invoiceQuery}
            placeholder="Buscar pelo número da invoice, ex. INV-2024-088"
            onChange={(e) => {
              setInvoiceQuery(e.target.value);
              setShowInvoiceMenu(true);
            }}
            onFocus={() => setShowInvoiceMenu(true)}
            aria-label="Buscar invoice da viagem"
          />
          {showInvoiceMenu && (
            <div className="orgcx-voyage__menu" role="listbox">
              {searchingInvoices && (
                <div className="orgcx-voyage__empty">Buscando viagens…</div>
              )}
              {!searchingInvoices && invoiceResults.length === 0 && (
                <div className="orgcx-voyage__empty">Nenhuma invoice encontrada</div>
              )}
              {invoiceResults.map((inv) => (
                <button
                  key={inv.id}
                  type="button"
                  className={`orgcx-voyage__item${
                    selectedInvoice?.id === inv.id ? ' is-active' : ''
                  }`}
                  onClick={() => pickInvoice(inv)}
                >
                  <strong>{inv.numero_documento || `Invoice #${inv.id}`}</strong>
                  <span>
                    {`${inv.origem || '—'} → ${inv.destino || '—'}`}
                    {Array.isArray(inv.pacotes)
                      ? ` · ${inv.pacotes.length} CX`
                      : ''}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
        {invoice && (
          <div className="orgcx-voyage__chip">
            <span className="orgcx-voyage__chip-code">
              {invoice.numero_documento}
            </span>
            <span>
              {invoice.origem || '—'} → {invoice.destino || '—'}
            </span>
            {invoice.status?.status_invoice && (
              <em>{invoice.status.status_invoice}</em>
            )}
          </div>
        )}
      </section>

      {!invoiceId && (
        <div className="orgcx-empty">
          <HiArchive size={42} />
          <h2>Selecione a viagem</h2>
          <p>
            Escolha a invoice em que os CX viajaram para montar as caixas
            físicas. Cada CX pode estar em uma ou várias caixas, sozinho ou
            misturado com outros clientes.
          </p>
        </div>
      )}

      {invoiceId && loadingBoard && (
        <div className="orgcx-loading">Montando o quadro da viagem…</div>
      )}

      {invoiceId && !loadingBoard && (
        <div className="orgcx-board">
          <aside className="orgcx-rail">
            <div className="orgcx-rail__head">
              <div>
                <h2>Pacotes CX</h2>
                <p>Toque e solte na caixa física, ou selecione e clique nela</p>
              </div>
              <span className="orgcx-count">{filteredPacotes.length}</span>
            </div>
            <div className="orgcx-rail__search">
              <HiSearch size={16} />
              <input
                type="search"
                value={pacoteFilter}
                onChange={(e) => setPacoteFilter(e.target.value)}
                placeholder="Filtrar CX, cliente ou caixa"
              />
            </div>
            <div className="orgcx-cx-list">
              {filteredPacotes.length === 0 && (
                <div className="orgcx-rail__empty">Nenhum CX nesta viagem</div>
              )}
              {filteredPacotes.map((pkg) => {
                const selected = selectedPacoteId === pkg.id;
                const assigned = Boolean(pkg.tem_caixa_fisica);
                return (
                  <button
                    key={pkg.id}
                    type="button"
                    draggable
                    className={`orgcx-cx${selected ? ' is-selected' : ''}${
                      assigned ? ' is-assigned' : ''
                    }`}
                    onClick={() =>
                      setSelectedPacoteId((cur) => (cur === pkg.id ? null : pkg.id))
                    }
                    onDragStart={() => setDraggingPacoteId(pkg.id)}
                    onDragEnd={() => {
                      setDraggingPacoteId(null);
                      setDropBoxId(null);
                    }}
                  >
                    <span className="orgcx-cx__code">{pacoteCode(pkg)}</span>
                    <span className="orgcx-cx__who">
                      {pacoteCliente(pkg)}
                      {pkg.cliente?.codigo ? ` · ${pkg.cliente.codigo}` : ''}
                    </span>
                    <span className="orgcx-cx__meta">
                      {assigned ? (
                        <>
                          {(pkg.caixas_fisicas || []).map((c) => (
                            <em key={c.numero || c.id}>Caixa {c.numero}</em>
                          ))}
                          {!(pkg.caixas_fisicas || []).length &&
                            (pkg.numeros_caixa || []).map((n) => (
                              <em key={n}>Caixa {n}</em>
                            ))}
                          {pkg.em_varias_caixas && (
                            <strong>Várias caixas</strong>
                          )}
                          {(pkg.tem_exclusivo ||
                            (pkg.caixas_fisicas || []).some((c) => !c.misturado)) && (
                            <strong className="is-solo">Inteiro</strong>
                          )}
                          {(pkg.tem_misturado ||
                            (pkg.caixas_fisicas || []).some((c) => c.misturado)) && (
                            <strong className="is-mix">Misturado</strong>
                          )}
                        </>
                      ) : (
                        <strong className="is-pending">Não atribuído</strong>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </aside>

          <section className="orgcx-stage">
            <div className="orgcx-stage__head">
              <div>
                <h2>Caixas físicas</h2>
                <p>
                  {selectedPacoteId
                    ? 'CX selecionado — clique numa caixa para atribuir (pode ir em mais de uma)'
                    : '1 CX = só este cliente · mais de 1 CX = misturado, automaticamente'}
                </p>
              </div>
              <button
                type="button"
                className="orgcx-btn orgcx-btn--primary"
                onClick={handleCreateBox}
                disabled={creatingBox}
              >
                <HiPlus size={18} />
                Nova caixa
              </button>
            </div>

            {caixas.length === 0 && (
              <div className="orgcx-stage__empty">
                <HiArchive size={36} />
                <h3>Nenhuma caixa física ainda</h3>
                <p>
                  Crie a Caixa 1 e vá atribuindo os CX. Se um cliente viajou em
                  mais de uma caixa, solte o mesmo CX nas duas.
                </p>
              </div>
            )}

            <div className="orgcx-boxes">
              {caixas.map((caixa) => {
                const isDrop = dropBoxId === caixa.id;
                const busy = savingBoxId === caixa.id;
                const picking = addMenuBoxId === caixa.id;
                const extras = picking ? pacotesDisponiveis(caixa) : [];
                const pacotesDaCaixa = byNewest(caixa.pacotes || [], 'atribuido_em');
                const isMix = pacotesDaCaixa.length > 1;
                return (
                  <article
                    key={caixa.id}
                    className={`orgcx-box${isMix ? ' is-mix' : ' is-solo'}${
                      isDrop ? ' is-drop' : ''
                    }${selectedPacoteId ? ' is-target' : ''}${
                      picking ? ' is-picking' : ''
                    }`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDropBoxId(caixa.id);
                    }}
                    onDragLeave={() => setDropBoxId((cur) => (cur === caixa.id ? null : cur))}
                    onDrop={(e) => {
                      e.preventDefault();
                      onDropPacote(caixa.id);
                    }}
                    onClick={() => {
                      if (selectedPacoteId) handleAssign(caixa.id, selectedPacoteId);
                    }}
                  >
                    <header className="orgcx-box__top">
                      <div className="orgcx-box__num">
                        <span>Caixa</span>
                        <strong>{caixa.numero}</strong>
                      </div>
                      <div className="orgcx-box__top-actions">
                        <span className="orgcx-box__qty">
                          {pacotesDaCaixa.length} CX
                        </span>
                        <button
                          type="button"
                          className="orgcx-icon-btn"
                          title="Excluir caixa física"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteTarget(caixa);
                          }}
                        >
                          <HiTrash size={16} />
                        </button>
                      </div>
                    </header>

                    <div
                      className="orgcx-seg orgcx-seg--auto"
                      onClick={(e) => e.stopPropagation()}
                      role="group"
                      aria-label="Ocupação da caixa, definida pelo número de CX"
                      title="1 CX = só este cliente. Mais de 1 CX = misturado."
                    >
                      <button
                        type="button"
                        className={!isMix ? 'is-on' : ''}
                        onClick={() => handleOcupacao(caixa, 'exclusivo')}
                        disabled={busy || isMix}
                        aria-pressed={!isMix}
                      >
                        <HiUser size={14} />
                        Só este cliente
                      </button>
                      <button
                        type="button"
                        className={isMix ? 'is-on is-mix' : ''}
                        onClick={() => handleOcupacao(caixa, 'misturado')}
                        disabled={busy || !isMix}
                        aria-pressed={isMix}
                      >
                        <HiOutlineUserGroup size={14} />
                        Misturado
                      </button>
                    </div>

                    <label className="orgcx-obs" onClick={(e) => e.stopPropagation()}>
                      <span>Observação para o entregador</span>
                      <textarea
                        rows={2}
                        value={obsDrafts[caixa.id] ?? ''}
                        placeholder="Ex.: fica no fundo, saco transparente…"
                        onChange={(e) =>
                          setObsDrafts((cur) => ({ ...cur, [caixa.id]: e.target.value }))
                        }
                        onBlur={() => handleSaveObs(caixa)}
                      />
                    </label>

                    <div
                      className="orgcx-box__add"
                      ref={picking ? addMenuRef : null}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        className={`orgcx-box__add-btn${picking ? ' is-open' : ''}`}
                        onClick={(e) => toggleAddMenu(caixa.id, e)}
                        disabled={busy}
                      >
                        <HiPlus size={16} />
                        Adicionar Pacote CX
                      </button>
                      {picking && (
                        <div className="orgcx-box__picker" role="listbox">
                          <div className="orgcx-box__picker-search">
                            <HiSearch size={14} />
                            <input
                              type="search"
                              autoFocus
                              value={addMenuQuery}
                              placeholder="Buscar CX ou cliente"
                              onChange={(e) => setAddMenuQuery(e.target.value)}
                              aria-label="Buscar pacote CX para esta caixa"
                            />
                          </div>
                          {extras.length === 0 && (
                            <div className="orgcx-box__picker-empty">
                              {addMenuQuery.trim()
                                ? 'Nenhum CX encontrado'
                                : 'Todos os CX desta viagem já estão nesta caixa'}
                            </div>
                          )}
                          {extras.map((pkg) => (
                            <button
                              key={pkg.id}
                              type="button"
                              className="orgcx-box__picker-item"
                              onClick={() => handleAssign(caixa.id, pkg.id)}
                            >
                              <strong>{pacoteCode(pkg)}</strong>
                              <span>
                                {pacoteCliente(pkg)}
                                {pkg.cliente?.codigo ? ` · ${pkg.cliente.codigo}` : ''}
                              </span>
                              {!pkg.tem_caixa_fisica && (
                                <em>Não atribuído</em>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="orgcx-box__list" onClick={(e) => e.stopPropagation()}>
                      {pacotesDaCaixa.length === 0 && (
                        <div className="orgcx-box__empty">
                          {selectedPacoteId
                            ? 'Clique para colocar o CX nesta caixa'
                            : 'Solte um CX aqui ou use Adicionar Pacote CX'}
                        </div>
                      )}
                      {pacotesDaCaixa.map((pkg) => {
                        const key = `${caixa.id}:${pkg.id}`;
                        return (
                          <div key={pkg.id} className="orgcx-chip">
                            <div className="orgcx-chip__main">
                              <strong>{pacoteCode(pkg)}</strong>
                              <span>{pacoteCliente(pkg)}</span>
                            </div>
                            <input
                              type="text"
                              value={pkgObsDrafts[key] ?? ''}
                              placeholder="Nota deste CX nesta caixa"
                              onChange={(e) =>
                                setPkgObsDrafts((cur) => ({
                                  ...cur,
                                  [key]: e.target.value,
                                }))
                              }
                              onBlur={() => handleSavePkgObs(caixa.id, pkg.id)}
                            />
                            <button
                              type="button"
                              className="orgcx-icon-btn"
                              title="Remover desta caixa"
                              onClick={() => handleDetach(caixa.id, pkg.id)}
                            >
                              <HiX size={14} />
                            </button>
                          </div>
                        );
                      })}
                    </div>

                    {busy && <div className="orgcx-box__busy">Salvando…</div>}
                  </article>
                );
              })}
            </div>
          </section>
        </div>
      )}

      <ConfirmDialog
        isOpen={Boolean(deleteTarget)}
        type="danger"
        title="Excluir caixa física"
        message={
          deleteTarget
            ? `A Caixa ${deleteTarget.numero} será removida. Os CX voltam para a lista se não estiverem em outra caixa.`
            : ''
        }
        confirmText="Excluir"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default OrganizarCaixa;
